import { MetricPeriodTypeCdEnum, MetricTypeCdEnum } from './expApiTypes'

export interface UserCorePreferences {
	readonly companyId: string // companyId = partyId
	readonly preferences: {
		readonly preferencesTypeCd: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
		readonly termsConditions: {
			readonly acceptedTermsFileNm: string
			readonly acceptedTermsTs: string
		}
	}
	readonly userId: string // oktaId
}

export interface UserBusIntelPreferences {
	userId?: string // oktaId
	companyId?: string // companyId = partyId
	preferences?: {
		preferencesTypeCd?: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
		performance?: {
			header?: {
				metricTypeCd?: string // Enum: "GCI", "RECRUIT", "SIDES", "TRXAM"
				metricPeriodTypeCd?: string // Enum: "ALL", "LTM", "MONTH", "YEAR", "YTD"
				hideGoalBannerYearNo?: number // year number when the user dismissed the goal banner permanently
				officeGoalWarningShownFl?: boolean // flag indicating if the office goal warning has been shown
			}
			benchmark?: {
				gciCategoryCd?: string // Enum: "0M", "10M", "1M", "20M", "5M", "UNK"
				showBenchmarkFl?: boolean // defaults to false
			}
			revenueChart?: {
				displayCategoryCds?: string[] // NEW <= 20 items; items <= 25 characters
			}
		}
		dealPipelineListings?: {
			metricPeriodTypeCd?: string // Enum: "LTM", "YTD"
			listingStatusCds?: string[] // NEW <= 15 items; items <= 4 characters
			listingCategoryCds?: string[] // NEW <= 15 items; items <= 25 characters
		}
		dealPipelineTransactions?: {
			metricPeriodTypeCd?: string // Enum: "LTM", "YTD"
			sideTypeCd?: string // Enum: "BUY", "SELL", "BOTH"
			transactionBreakdownGraphs?: [
				{
					transactionStatusCd: string // Enum: "OPND", "CLOS"
					metricTypeCd: string // Enum: "ABCR", "AVGSALEAM", "GCI", "SIDES", "TRXAM"
				},
			]
		}
	}
}

export interface UserAgentProdPreferences {
	userId?: string // oktaId
	companyId?: string // companyId = partyId
	preferences?: {
		preferencesTypeCd?: string // Enum: "AGENTPROD"
		agentProductivityPrefs?: {
			metricPeriodTypeCd?: string // Enum: "LTM", "YTD"
			metricTypeCd?: string // Enum: "GCI", "SIDES", "TRXAM"
			productionCategoryCd?: string // ENUM: "ALL", "PROD", "NONPROD"
		}
	}
}

export interface UserRecruitPreferences {
	userId?: string // oktaId
	companyId?: string // companyId = partyId
	preferences?: {
		preferencesTypeCd?: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
		mlsSearch?: {
			mlsIds?: string | null // Comma seperated list of MLS IDs
			productionPostalCds?: string | null // Comma seperated list of postal codes
			brokerageNms?: string | null // Comma seperated list of brokerage names
			brandCds?: string | null // Comma seperated list of brand codes
			minAvgSalePriceAm?: number | null // Lowest value average sale price
			maxAvgSalePriceAm?: number | null // Highest value average sale price
			minClosedSideCt?: number | null // Lowest value closed side count
			maxClosedSideCt?: number | null // Highest value closed side count
			minGCIAm?: number | null // Lowest value estimated GCI amount
			maxGCIAm?: number | null // Highest value estimated GCI amount
			minOpenListingCt?: number | null // The lowest number of open listings in the time period
			maxOpenListingCt?: number | null // The highest number of open listings in the time period
			minSalesVolumeAm?: number | null // The lowest value of sales volume in the time period
			maxSalesVolumeAm?: number | null // The highest value of sales volume in the time period
			sisterBrandAgentFl?: boolean | null // Identifies if an agent is affiliated with an Anywhere brand or not
			repFocusCd?: string | null // Enum: BAL, BUY, SELL
			minMLSFirstActiveDt?: string | null // The date on or after which the agent must have become active in the MLS.
			maxMLSFirstActiveDt?: string | null // The date on or before which the agent must have become active in the MLS.
			agentNm?: string | null // The full name by which an agent is called.
			cobrokerOnlyFl?: boolean // A boolean value determining if an agent is only a co-broker.
			apexLevelCd?: string | null // A flag to include high potential agents in search results.
			excludeCurrentAgentsFl?: boolean | null // A boolean flag to exclude current agents from search results.
			formerAgentFl?: boolean | null // A boolean flag to include former agents in search results.
			exBrandAgentCds?: string | null // Brand code to filter former CBR agents by brand.
			exOwnedAgentFl?: boolean | null // A boolean flag to include former CBR Anywhere agents in search results.
			mlsStatusCd?: string | null // Enum: ACTV, NACTV - MLS membership status code for an agent.
		}
	}
}

export interface UserCorePrefsQuery {
	readonly preferencesTypeCd?: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
	readonly partyId?: string
	readonly companyId?: string // companyId = partyId
	readonly acceptedTermsFileNm?: string
	readonly acceptedTermsTs?: string
}

export interface UserBusIntelPrefsQuery {
	readonly preferencesTypeCd?: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
	readonly partyId?: string
}

export interface UserRecruitPrefsQuery {
	readonly preferencesTypeCd?: string // Enum: "BUSINTEL", "CORE", "RECRUIT"
	readonly partyId?: string
}

export const DEFAULTBUSINTELPREFS: UserBusIntelPreferences = {
	preferences: {
		preferencesTypeCd: 'BUSINTEL',
		performance: {
			header: {
				metricTypeCd: MetricTypeCdEnum.GCI,
				metricPeriodTypeCd: MetricPeriodTypeCdEnum.YTD,
			},
		},
		dealPipelineListings: {},
		dealPipelineTransactions: {},
	},
}

export const DEFAULTAGENTPRODPREFS: UserAgentProdPreferences = {
	preferences: {
		preferencesTypeCd: 'AGENTPROD',
		agentProductivityPrefs: {},
	},
}

export const DEFAULTRECRUITPREFS: UserRecruitPreferences = {
	preferences: {
		preferencesTypeCd: 'RECRUIT',
		mlsSearch: {
			mlsIds: null,
			productionPostalCds: null,
			brokerageNms: null,
			brandCds: null,
			minAvgSalePriceAm: null,
			maxAvgSalePriceAm: null,
			minClosedSideCt: null,
			maxClosedSideCt: null,
			minGCIAm: null,
			maxGCIAm: null,
			minOpenListingCt: null,
			maxOpenListingCt: null,
			minSalesVolumeAm: null,
			maxSalesVolumeAm: null,
			sisterBrandAgentFl: true,
			repFocusCd: null,
			minMLSFirstActiveDt: null,
			maxMLSFirstActiveDt: null,
			agentNm: null,
			cobrokerOnlyFl: false,
			excludeCurrentAgentsFl: false,
		},
	},
}
